import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Truck } from 'lucide-react';
import clsx from 'clsx';
import {
  getVehicleTypes,
  createVehicleType,
  updateVehicleType,
  deleteVehicleType,
} from '../api/vehicleTypes';
import { useToast } from '../context/ToastContext';
import { getErrorMessage } from '../lib/utils';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import TypeToConfirmDialog from '../components/ui/TypeToConfirmDialog';
import { Skeleton } from '../components/ui/Skeleton';

const EMPTY_FORM = { name: '', is_active: true };

function VehicleTypeCard({ vehicleType, onEdit, onDelete }) {
  const active = vehicleType.is_active !== false;
  return (
    <Card padding={false} className={clsx('overflow-hidden transition-opacity', !active && 'opacity-70')}>
      <div className="p-5 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={clsx('shrink-0 rounded-xl p-2.5', active ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-gray-700')}>
            <Truck size={20} className={active ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 capitalize leading-tight truncate">{vehicleType.name}</h3>
            <p className="text-xs font-mono text-gray-400 dark:text-gray-500 mt-0.5">#{vehicleType.id}</p>
          </div>
        </div>
        <span
          className={clsx(
            'shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
            active
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
          )}
        >
          <span className={clsx('h-1.5 w-1.5 rounded-full', active ? 'bg-green-500' : 'bg-gray-400')} />
          {active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="flex items-stretch border-t border-gray-100 dark:border-gray-700">
        <button
          onClick={() => onEdit(vehicleType)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <Pencil size={14} /> Edit
        </button>
        <div className="w-px bg-gray-100 dark:bg-gray-700" />
        <button
          onClick={() => onDelete(vehicleType)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>
    </Card>
  );
}

export default function VehicleTypes() {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(() => searchParams.get('action') === 'create');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['vehicle-types', 'all'],
    queryFn: () => getVehicleTypes({ include_inactive: true }).then((r) => r.data),
  });

  const vehicleTypes = Array.isArray(data) ? data : (data?.items ?? []);

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
  }

  const create = useMutation({
    mutationFn: createVehicleType,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicle-types'] });
      addToast('Vehicle type created!', 'success');
      closeModal();
    },
    onError: () => addToast('Failed to create', 'error'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => updateVehicleType(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicle-types'] });
      addToast('Vehicle type updated!', 'success');
      closeModal();
    },
    onError: () => addToast('Failed to update', 'error'),
  });

  const remove = useMutation({
    mutationFn: deleteVehicleType,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicle-types'] });
      addToast('Vehicle type deleted', 'success');
      setDeleteTarget(null);
    },
    onError: (e) => addToast(getErrorMessage(e, 'Failed to delete vehicle type'), 'error'),
  });

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({ name: row.name ?? '', is_active: row.is_active !== false });
    setErrors({});
    setModalOpen(true);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErrors({ name: 'Name is required' }); return; }
    setErrors({});
    if (editingId) {
      update.mutate({ id: editingId, data: { name: form.name.trim(), is_active: form.is_active } });
    } else {
      create.mutate({ name: form.name.trim() });
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Vehicle Types</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Types drivers can register and fare configs attach to</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>Add Type</Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <Skeleton className="h-6 w-1/2 mb-4" />
              <Skeleton className="h-5 w-1/3 mb-4" />
              <Skeleton className="h-8 w-2/3" />
            </Card>
          ))}
        </div>
      ) : vehicleTypes.length === 0 ? (
        <Card className="py-12 text-center text-gray-400">No vehicle types defined</Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicleTypes.map((vt) => (
            <VehicleTypeCard
              key={vt.id}
              vehicleType={vt}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Edit Vehicle Type' : 'New Vehicle Type'}
        size="sm"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={errors.name}
            placeholder="e.g. Luxury"
          />
          {editingId && (
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-amber-500 focus:ring-amber-500"
              />
              Active
            </label>
          )}
          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {editingId ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      <TypeToConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Vehicle Type"
        message={`This permanently deletes "${deleteTarget?.name}". If it's still referenced by any vehicles, fare configs, plans, or rides, the delete will be blocked — deactivate it instead by editing it and unchecking Active.`}
        confirmWord={deleteTarget?.name}
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => remove.mutate(deleteTarget.id)}
      />
    </div>
  );
}
